import { getConsent, hasConsent, type CookieCategory } from "@/lib/cookies/consent"

// ---------------------------------------------------------------------------
// Ecommerce tracking helper: one typed entry point for the ad/analytics events
// (GA4 via gtag, Meta pixel via fbq). Components call trackViewItem() etc. and
// never touch window.gtag / window.fbq directly.
//
// Guarantees enforced in THIS file:
//  - Consent: a GA4 event is only sent while the `analytics` category is granted,
//    a Meta event only while `marketing` is granted (checked at send time).
//  - No tag, no call: nothing is sent until the destination announces itself
//    ready via markAnalyticsReady(). GoogleAnalytics.tsx calls it after
//    gtag('config'); MetaPixel.tsx after fbq('init'). Events raised earlier
//    (e.g. `purchase` on a full page load, before gtag.js has loaded, or while
//    the cookie banner is still undecided) wait in a small in-memory queue and
//    are flushed on ready. An explicit refusal drops the event instead.
//  - `purchase` fires at most once per transaction id per browser
//    (localStorage list, in-memory fallback), so a reload, back navigation or
//    revisit of the confirmation page does not double-count revenue.
// ---------------------------------------------------------------------------

export type AnalyticsDestination = "ga" | "meta"

export interface AnalyticsItem {
  /** SKU */
  item_id: string
  item_name: string
  item_brand?: string
  item_category?: string
  /** Unit price, gross (incl. VAT), matching what the storefront shows. */
  price: number
  quantity: number
}

export interface EcommercePayload {
  currency: string
  value: number
  items: AnalyticsItem[]
}

export interface PurchasePayload extends EcommercePayload {
  /** Order increment id. Also the Meta `eventID` for browser/server dedupe. */
  transaction_id: string
  tax?: number
  shipping?: number
  coupon?: string
}

type Fbq = (...args: unknown[]) => void

declare global {
  interface Window {
    fbq?: Fbq
  }
}

const CONSENT_FOR: Record<AnalyticsDestination, CookieCategory> = {
  ga: "analytics",
  meta: "marketing",
}

const MAX_QUEUE = 50
const PURCHASE_STORAGE_KEY = "appslu:tracked-purchases"
const MAX_REMEMBERED_PURCHASES = 50

const ready: Record<AnalyticsDestination, boolean> = { ga: false, meta: false }
const queues: Record<AnalyticsDestination, Array<() => void>> = { ga: [], meta: [] }
const trackedInMemory = new Set<string>()

function dispatch(destination: AnalyticsDestination, send: () => void): void {
  if (typeof window === "undefined") return
  const category = CONSENT_FOR[destination]
  if (!hasConsent(category)) {
    // Visitor answered the banner and refused this category: drop for good.
    // No answer yet: hold the event, it is sent only if they grant it.
    if (getConsent() !== null) return
    if (queues[destination].length < MAX_QUEUE) queues[destination].push(send)
    return
  }
  if (ready[destination]) {
    send()
    return
  }
  if (queues[destination].length < MAX_QUEUE) queues[destination].push(send)
}

/**
 * Called by the tag component once its library can take events. Flushes
 * anything queued, provided consent for that destination is granted now.
 */
export function markAnalyticsReady(destination: AnalyticsDestination): void {
  ready[destination] = true
  const pending = queues[destination].splice(0)
  if (!hasConsent(CONSENT_FOR[destination])) return
  for (const send of pending) send()
}

function gaEvent(name: string, params: Record<string, unknown>): void {
  dispatch("ga", () => {
    if (typeof window.gtag === "function") window.gtag("event", name, params)
  })
}

function metaEvent(name: string, params: Record<string, unknown>, eventId?: string): void {
  dispatch("meta", () => {
    if (typeof window.fbq !== "function") return
    if (eventId) window.fbq("track", name, params, { eventID: eventId })
    else window.fbq("track", name, params)
  })
}

function metaContents(payload: EcommercePayload): Record<string, unknown> {
  return {
    content_ids: payload.items.map((i) => i.item_id),
    contents: payload.items.map((i) => ({
      id: i.item_id,
      quantity: i.quantity,
      item_price: i.price,
    })),
    content_type: "product",
    value: payload.value,
    currency: payload.currency,
  }
}

export function trackViewItem(payload: EcommercePayload): void {
  if (payload.items.length === 0) return
  gaEvent("view_item", { ...payload })
  metaEvent("ViewContent", {
    ...metaContents(payload),
    content_name: payload.items[0].item_name,
  })
}

export function trackAddToCart(payload: EcommercePayload): void {
  if (payload.items.length === 0) return
  gaEvent("add_to_cart", { ...payload })
  metaEvent("AddToCart", metaContents(payload))
}

export function trackBeginCheckout(payload: EcommercePayload): void {
  if (payload.items.length === 0) return
  gaEvent("begin_checkout", { ...payload })
  metaEvent("InitiateCheckout", {
    ...metaContents(payload),
    num_items: payload.items.reduce((n, i) => n + i.quantity, 0),
  })
}

/** Returns true the first time it sees this id in this browser, false after. */
function claimPurchase(transactionId: string): boolean {
  if (trackedInMemory.has(transactionId)) return false
  let remembered: string[] = []
  try {
    const raw = window.localStorage.getItem(PURCHASE_STORAGE_KEY)
    const parsed: unknown = raw ? JSON.parse(raw) : []
    if (Array.isArray(parsed)) remembered = parsed.filter((v): v is string => typeof v === "string")
  } catch {
    // Storage blocked or corrupt: the in-memory set still dedupes this page life.
  }
  trackedInMemory.add(transactionId)
  if (remembered.includes(transactionId)) return false
  try {
    const next = [...remembered, transactionId].slice(-MAX_REMEMBERED_PURCHASES)
    window.localStorage.setItem(PURCHASE_STORAGE_KEY, JSON.stringify(next))
  } catch {
    // Ignore: see above.
  }
  return true
}

export function trackPurchase(payload: PurchasePayload): void {
  if (typeof window === "undefined") return
  if (!payload.transaction_id) return
  if (!claimPurchase(payload.transaction_id)) return
  gaEvent("purchase", { ...payload })
  metaEvent(
    "Purchase",
    {
      ...metaContents(payload),
      num_items: payload.items.reduce((n, i) => n + i.quantity, 0),
    },
    payload.transaction_id,
  )
}

/** Test-only: reset module state between tests. */
export function __resetAnalyticsForTests(): void {
  ready.ga = false
  ready.meta = false
  queues.ga.length = 0
  queues.meta.length = 0
  trackedInMemory.clear()
}
